import { DeleteOutlined, LinkOutlined, WarningFilled } from "@ant-design/icons"
import { Flex, Space, Table, Tag, Tooltip, Typography } from "antd"
import type { ItemType } from "antd/es/menu/interface"
import { type ColumnsType } from "antd/es/table"
import { type BaseType } from "antd/es/typography/Base"
import React from "react"

import Icon from "../../../../../../../Icon"
import { RowActionsMenu } from "../../../../../../../components/EntityTable/RowActionsMenu.tsx"
import { empty } from "../../../../../../../constants/placeholders"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import { DATE_FORMAT } from "../../../../../../../utils/date/date.ts"
import {
  type Attachment,
  AttachmentModels
} from "../../../../../../Album/types/attachment.types.ts"
import { AttachmentsController } from "../../../../../components/AttachmentsController.tsx"
import { type MilestoneScheduleRisk } from "../../../../../redux/project-milestones/project-milestones.types.ts"
import { type calculateEffectiveSchedule } from "../../../../utils/milestone-effective-schedule.ts"
import type { EffectiveScheduleEntry } from "../../../../utils/milestone-effective-schedule.ts"
import { MilestoneRiskTag } from "./MilestoneRiskTag.tsx"
import { type ProjectMilestone } from "../../../../../types/project-phases.types.tsx"
import {
  MILESTONE_STATUSES_MAP,
  type MilestoneStatuses
} from "../../../../constants/milestone-statuses.ts"
import {
  PROJECT_ENTITY_CATEGORIES_MAP,
  type ProjectEntityCategories
} from "../../../../constants/project-entity-category.ts"
import { MilestoneFinancialCell } from "./MilestoneFinancialCell.tsx"

const { Text, Paragraph } = Typography

interface GetColumnsColumnsArgs {
  currentUserId: string
  //
  deleteAllowed?: boolean
  editAllowed?: boolean
  uploadFileAllowed?: boolean
  deleteFileAllowed?: boolean
  deleteForeignFileAllowed?: boolean
  //
  isLocked: boolean
  projectId: string
  /** The same dependency-aware schedule the Gantt renders from — a Planned
   * Start/End that would otherwise violate a dependency shows its
   * recalculated date here too, so the two views never disagree. */
  effectiveSchedule?: ReturnType<typeof calculateEffectiveSchedule>
  userFormat?: string | null
  onSelectRow: (record: ProjectMilestone, action: string) => void
  contractValue?: number | null
  contractExpenses?: number | null
}

const renderPlannedDate = (
  side: "start" | "end",
  value: string,
  record: ProjectMilestone,
  effectiveSchedule: ReturnType<typeof calculateEffectiveSchedule> | undefined,
  userFormat: string | null | undefined
) => {
  const entry: EffectiveScheduleEntry | undefined = effectiveSchedule?.get(record.id)

  if (!value) {
    if (!entry?.isGenerated) return empty
    const generatedDate = side === "start" ? entry.start : entry.end
    return (
      <Space size={4}>
        <Text type="secondary">{generatedDate.format(userFormat || DATE_FORMAT)}</Text>
        <Tooltip title="No date set — scheduled automatically from the milestone it depends on.">
          <LinkOutlined className="text-secondary" />
        </Tooltip>
      </Space>
    )
  }

  if (!entry?.isShifted) return value

  const effectiveDate = side === "start" ? entry.start : entry.end
  const displayValue = effectiveDate.format(userFormat || DATE_FORMAT)

  return (
    <Space size={4}>
      <Text>{displayValue}</Text>
      <Tooltip
        title={`Originally planned ${value} — shifted because it depends on a milestone that finishes later.`}
      >
        <WarningFilled className="text-warning" />
      </Tooltip>
    </Space>
  )
}

// The row-number column's key. The inline-edit row reads it to know where the
// table's own leading cells end, so the two must name the same column.
export const ORDER_COLUMN_KEY = "order"

export const getColumns = ({
  currentUserId,
  projectId,
  isLocked,
  //
  deleteAllowed,
  editAllowed,
  uploadFileAllowed,
  deleteFileAllowed,
  deleteForeignFileAllowed,
  //
  effectiveSchedule,
  userFormat,
  onSelectRow,
  contractValue,
  contractExpenses
}: GetColumnsColumnsArgs): ColumnsType<ProjectMilestone> => [
  Table.EXPAND_COLUMN,
  {
    title: "#",
    dataIndex: "order",
    key: ORDER_COLUMN_KEY,
    width: 40,
    align: "center",
    // render: (order: number) => order
    render: (_order: number, _record: ProjectMilestone, index: number) =>
      index + 1
  },
  {
    title: "Milestone Name",
    dataIndex: "name",
    key: "name",
    width: 220,
    render: (name: string) => {
      return (
        <div className={"max-w-56 w-fit relative"}>
          <Text ellipsis={{ tooltip: name }}>{name}</Text>
        </div>
      )
    }
  },
  {
    title: "Type",
    dataIndex: "milestone_category",
    key: "milestone_category",
    width: 165,
    render: (category: ProjectEntityCategories, record) => {
      const data = PROJECT_ENTITY_CATEGORIES_MAP?.[category]

      return <Tag style={data?.style}>{record?.milestone_category_display}</Tag>
    }
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    // Wide enough for the inline-edit select this column holds. Declared
    // narrower, the select pushes the column out and shifts every column to
    // its right the moment a row opens for edit.
    width: 130,
    render: (status: MilestoneStatuses, record) => {
      const statusData = MILESTONE_STATUSES_MAP?.[status]

      return <Tag style={statusData?.style}>{record?.status_display}</Tag>
    }
  },
  {
    title: "Risk",
    dataIndex: "schedule_risk",
    key: "schedule_risk",
    width: 110,
    render: (risk?: MilestoneScheduleRisk) => <MilestoneRiskTag risk={risk} />
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    width: 260,
    onCell: () => ({ style: { minWidth: 200, maxWidth: 260 } }),
    render: (description: string) => (
      <Paragraph
        className={"mb-0"}
        ellipsis={{ rows: 3, tooltip: description }}
      >
        {description || empty}
      </Paragraph>
    )
  },
  {
    title: "Receivables",
    key: "receivables",
    width: 190,
    align: "center",
    onCell: () => ({ style: { whiteSpace: "nowrap" } }),
    render: (_, record) => (
      <MilestoneFinancialCell
        settled={record?.actual_received}
        planned={record?.planned_receivable}
        committed={record?.committed_receivable}
        contractLimit={contractValue}
        contractLabel="Contract Value"
      />
    )
  },
  {
    title: "Payables",
    key: "payables",
    width: 190,
    align: "center",
    onCell: () => ({ style: { whiteSpace: "nowrap" } }),
    render: (_, record) => (
      <MilestoneFinancialCell
        settled={record?.actual_paid}
        planned={record?.planned_payable}
        committed={record?.committed_payable}
        contractLimit={contractExpenses}
        contractLabel="Contract Expenses"
      />
    )
  },
  {
    title: "Planned Start",
    dataIndex: "start_date",
    key: "start_date",
    width: 135,
    align: "center",
    render: (value: string, record) =>
      renderPlannedDate("start", value, record, effectiveSchedule, userFormat)
  },
  {
    title: "Planned End",
    dataIndex: "end_date",
    key: "end_date",
    width: 135,
    align: "center",
    render: (value: string, record) =>
      renderPlannedDate("end", value, record, effectiveSchedule, userFormat)
  },
  {
    title: "Actual End",
    dataIndex: "actual_end_date",
    key: "actual_end_date",
    width: 145,
    align: "center",
    render: (value: string, record) => {
      const variance = record?.variance || 0
      const isLate = variance < 0

      return (
        <Space>
          <Text>{value || empty}</Text>
          {isLate && (
            <Tooltip title={"Milestone is late"}>
              <WarningFilled className={"text-warning"} />
            </Tooltip>
          )}
        </Space>
      )
    }
  },
  {
    title: "Variance",
    dataIndex: "variance",
    key: "variance",
    width: 80,
    align: "center",
    render: (value: number | null) => {
      if (!value) return empty

      const settings: Record<
        number,
        {
          simbol: string
          color: BaseType | undefined
        }
      > = {
        [-1]: {
          simbol: "",
          color: "danger"
        },
        [0]: {
          simbol: "",
          color: undefined
        },
        [1]: {
          simbol: "+",
          color: "success"
        }
      }

      const settingsData = settings?.[Math.sign(value)]

      return (
        <Tag>
          <Text strong type={settingsData?.color}>
            {settingsData?.simbol}
            {value}d
          </Text>
        </Tag>
      )
    }
  },
  {
    title: "Dependencies",
    key: "dependencies",
    width: 90,
    align: "center",
    render: (_, record) => {
      const count = record?.dependency_count ?? 0
      if (!count) return <Text type="secondary">{empty}</Text>
      return (
        <Typography.Link
          onClick={() => onSelectRow(record, "manage_dependencies")}
          className="inline-flex items-center gap-1"
          aria-label={`${count} ${count === 1 ? "dependency" : "dependencies"}`}
        >
          <LinkOutlined /> {count}
        </Typography.Link>
      )
    }
  },
  {
    title: "Evidence",
    key: "attachments",
    dataIndex: "attachments",
    width: 80,
    align: "center",
    render: (attachments: Attachment[], record) => (
      <AttachmentsController
        currentUserId={currentUserId}
        // addAllowed={uploadFileAllowed || editAllowed}
        addAllowed={editAllowed}
        deleteAllowed={deleteFileAllowed}
        deleteForeignAllowed={deleteForeignFileAllowed}
        project_id={projectId}
        model_type={AttachmentModels.MILESTONE}
        object_id={record?.id}
        attachments={attachments}
      />
    )
  },
  {
    title: "Actions",
    width: 50,
    align: "center",
    fixed: "right",
    hidden: !deleteAllowed && !editAllowed,
    render: (_, record) => (
      <Flex gap={"small"} justify={"center"} className={"w-20 mx-auto"}>
        <RowActionsMenu
          disabled={isLocked}
          items={
            [
              {
                key: "edit_row",
                label: "Edit",
                hidden: !editAllowed,
                icon: <Icon type={"Edit"} />,
                onClick: () => onSelectRow(record, "edit_row")
              },
              {
                key: "manage_dependencies",
                label: "Dependencies",
                hidden: !editAllowed,
                icon: <LinkOutlined />,
                onClick: () => onSelectRow(record, "manage_dependencies")
              },
              {
                key: "delete_row",
                label: "Delete",
                hidden: !deleteAllowed,
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => onSelectRow(record, "delete_row")
              }
            ] as Array<ItemType & { hidden?: boolean }>
          }
        />
      </Flex>
    )
  }
]
